import { useEffect, useState } from 'react';
import {
	Sliders,
	PlusSquare,
	File,
	ArrowLeftCircle,
	ArrowRightCircle,
	User,
} from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClients, fetchClientById } from '../slices/clientSlice';
import ClientDetails from '../components/clients/ClientDetails';
import dotYellow from '../assets/dot-yellow.svg';
import dotGreen from '../assets/dot-green.svg';
import dotRed from '../assets/dot-red.svg';

import ClientHubCreateClientPanel from '../components/clientHub/ClientHubCreateClientPanel';
import NewClient from '../components/clients/NewClient';
import { formatPhoneNumber, removePhoneFormatting } from '../hooks/phoneFormat';
import { fetchSelf, fetchUsers } from '../slices/userSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import useDebounce from '../hooks/useDebounce';

export default function ClientsContent() {
	const dispatch = useDispatch();
	const clients = useSelector((state) => state.clients.items);
	const clientById = useSelector((state) => state.clients.clientById);
	const [selectedClient, setSelectedClient] = useState(null);
	const [showCreateClientPanel, setShowCreateClientPanel] = useState(false);
	const [newCLientDescription, setNewClientDescription] = useState(null);
	const usersList = useSelector((state) => state.auth?.users);
	// Search bar
	const [filterFirstName, setFilterFirstName] = useState('');
	const [filterLastName, setFilterLastName] = useState('');
	const [filterCompanyName, setFilterCompanyName] = useState('');
	const [filterPhone, setFilterPhone] = useState('');
	const [filterStatus, setFilterStatus] = useState('');
	const [filterSource, setFilterSource] = useState('');

	const [currentPage, setCurrentPage] = useState(1);
	// Toggle sections
	const [filterSection, setFilterSections] = useState({
		filterOptions: true,
	});
	const [pageSize, setPageSize] = useState(100);
	const [sortOrder, setSortOrder] = useState('ASC');

	const clientIdString = localStorage.getItem('clientId');
	const clientId = JSON.parse(clientIdString)?.id;

	// Checking if the user is logged in
	// const user = useSelector((state) => state.user);
	const navigate = useNavigate();
    const location = useLocation();

    const handleNewClient = async () => {
		setShowCreateClientPanel(true);
	};

    useEffect(() => {
        if (location.state?.openCreateModal) {
            handleNewClient();
            // Clear state so it doesn't open again on refresh
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location]);

	useEffect(() => {
		dispatch(fetchUsers({ pageSize: 100, sortOrder: 'ASC' }));
	}, [dispatch]);

	// Debounced search term
	const debouncedSearchTerm = useDebounce(filterFirstName, 500);

	useEffect(() => {
        // Fetch a large batch to enable client-side filtering of messy phone numbers
		dispatch(
			fetchClients({
				pageSize: 1000,
				page: 1, // Always fetch the first 'all' batch
				// Remove server filters to get all data
			})
		);
	}, [dispatch]); // Only run on mount

	// Remove the localStorage useEffect as it might interfere with manual opening
	// and cause flickering/resets.

	const openClient = (client) => {
		const id = client?.id || client?.Id || client?._id;
		if (id) {
			setSelectedClient(id);
			dispatch(fetchClientById(id));
		}
	};

	const closeDetails = (clientIdOverride) => {
		setShowCreateClientPanel(false);
		const resolvedClientId =
			clientIdOverride || JSON.parse(localStorage.getItem('clientId') || '{}')?.id;
		setNewClientDescription(resolvedClientId || null);
		if (resolvedClientId) {
			setSelectedClient(resolvedClientId);
			dispatch(fetchClientById(resolvedClientId));
		}
	};

	const closeSlide = () => {
		console.log('Closing client panel');
		setSelectedClient(null);
		localStorage.removeItem('clientId');
	};

	// user list

	const accountManager = usersList
		?.filter((user) => !user?.email.includes('_deactivate_'))
		.map((user) => ({
			// label: user.firstName + ' ' + user.lastName,
			value: user.id,
			label: user.firstName + ' ' + user.lastName,
		}));

    const handleClientReady = async (clientId) => {
        setShowCreateClientPanel(false);
        if (clientId) {
            setSelectedClient(clientId);
            dispatch(fetchClientById(clientId));
        }
        dispatch(fetchClients({ pageSize: 1000, page: 1, sortOrder: 'DESC' }));
    };

	// Client-side filtering logic
    const filteredClients = clients?.filter(client => {
        // 1. Phone Filtering (Format Agnostic)
        if (filterPhone) {
            const queryPhone = filterPhone.replace(/\D/g, '');
            const clientPhone = (client.phoneNumber || '').replace(/\D/g, '');
            if (queryPhone.length > 0 && !clientPhone.includes(queryPhone)) {
                return false;
            }
        }

        // 2. Name Filtering
        if (filterFirstName) {
            const query = filterFirstName.toLowerCase();
            if (!(client.firstName || '').toLowerCase().includes(query)) return false;
        }

        // 3. Last Name Filtering
        if (filterLastName) {
            const query = filterLastName.toLowerCase();
            if (!(client.lastName || '').toLowerCase().includes(query)) return false;
        }

         // 4. Company Filtering
         if (filterCompanyName) {
            const query = filterCompanyName.toLowerCase();
            if (!(client.companyName || '').toLowerCase().includes(query)) return false;
        }
        
        // 5. Status Filtering
        if (filterStatus) {
             if (client.status != filterStatus) return false;
        }

        // 6. Source/Email Filtering (reusing filterSource variable if needed, or matching API behavior)
        if (filterSource) {
             const query = filterSource.toLowerCase();
             // Assuming filterSource maps to source or email? API called it 'source'.
             if (!(client.source || '').toLowerCase().includes(query)) return false;
        }
        
        return true;
    });

    // Local Pagination (Optional: Slice filteredClients)
    // For now, let's map filteredClients directly or slice it if needed.
    // Given 1000 items, we might want to slice it for valid rendering performance, 
    // but React handles 1000 rows okay. Let's slice for the view.
    const ITEMS_PER_PAGE = pageSize; 
    // Wait, pageSize state variable is used for "10/50/100".
    
    // We need to paginate the STATIC list `filteredClients`.
    const totalItems = filteredClients?.length || 0;
    const totalPages = Math.ceil(totalItems / pageSize);
    const paginatedClients = filteredClients?.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );


    // Handlers need to be purely local now
	const applyFilters = () => {
        setCurrentPage(1);
	};

	const resetFilters = () => {
		setFilterFirstName('');
		setFilterLastName('');
		setFilterCompanyName('');
		setFilterPhone('');
		setFilterStatus('');
		setFilterSource('');
        setCurrentPage(1);
	};


	// Toggle sections

	const toggleSection = (filterSection) => {
		setFilterSections((prevSections) => ({
			...prevSections,
			[filterSection]: !prevSections[filterSection],
		}));
	};

	const optionStatus = [
		{ id: 1, name: 'Pending' },
		{ id: 2, name: 'Active' },
		{ id: 3, name: 'Block' },
	];

	// Pagination Handlers
	const handleNextPage = () => {
		if (currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
        }
	};

	const handlePreviousPage = () => {
		if (currentPage > 1) {
			setCurrentPage((prev) => prev - 1);
		}
	};

	const handlePageSizeChange = (event) => {
		const newPageSize = parseInt(event.target.value, 10);
		setPageSize(newPageSize);
		setCurrentPage(1); 
	};


	return (
		<>
			<main className="flex-1 overflow-y-auto bg-slate-50 modern-dashboard">
			<div className="content-header">
				<h1>
					<User size={32} /> Clients Management
				</h1>
			</div>

			<div className="table-card">
				<div className="action-row">
					<div className="search-wrapper">
						<Sliders size={18} />
						<input 
							type="text" 
							placeholder="Quick search by name or company..." 
							value={filterFirstName}
							onChange={(e) => {
								setFilterFirstName(e.target.value);
								setCurrentPage(1); // Reset page on search
							}}
						/>
					</div>
					<button 
						type="button"
						className="client-management-btn client-management-btn--secondary"
						onClick={handleNewClient}
					>
						<PlusSquare size={18} />
						Add New Client
					</button>
				</div>

				{filterSection.filterOptions && (
					<div className="filters-grid">
						<input
							type="text"
							placeholder="First name"
							value={filterFirstName ?? ''}
							onChange={(e) => setFilterFirstName(e.target.value)}
						/>
						<input
							type="text"
							placeholder="Last name"
							value={filterLastName ?? ''}
							onChange={(e) => setFilterLastName(e.target.value)}
						/>
						<input
							type="text"
							placeholder="Company"
							value={filterCompanyName ?? ''}
							onChange={(e) => setFilterCompanyName(e.target.value)}
						/>
						<input
							type="text"
							placeholder="Phone"
							value={formatPhoneNumber(filterPhone) ?? ''}
							onChange={(e) => setFilterPhone(e.target.value)}
						/>
						<select
							value={filterStatus}
							onChange={(e) => setFilterStatus(e.target.value)}
						>
							<option value="">Status</option>
							{optionStatus.map((status) => (
								<option key={status.id} value={status.id}>
									{status.name}
								</option>
							))}
						</select>
						
						<div className="filter-btn-group">
							<button onClick={applyFilters} className="search">Search</button>
							<button onClick={resetFilters} className="reset">Reset</button>
						</div>
					</div>
				)}

				<table className="modern-table">
					<thead>
						<tr>
							<th>Full Name / Company</th>
							<th>Email</th>
							<th>Phone</th>
							<th>Status</th>
							<th className="text-right">Actions</th>
						</tr>
					</thead>
					<tbody>
						{paginatedClients?.map((client, index) => (
							<tr
								key={client?.id || index}
								onClick={() => openClient(client)}
								className="cursor-pointer hover:bg-slate-50 transition-colors"
							>
								<td>
									<div className="flex items-center gap-3">
										<div className={`avatar-initials bg-avatar-${(index % 5) + 1}`}>
											{(client?.firstName?.[0] || '') + (client?.lastName?.[0] || '')}
										</div>
										<div>
											<div className="font-semibold text-slate-800">
												{client?.firstName} {client?.lastName}
											</div>
											{client?.companyName && (
												<div className="text-xs text-slate-500 font-medium">
													{client?.companyName}
												</div>
											)}
										</div>
									</div>
								</td>
								<td>{client?.email}</td>
								<td>{formatPhoneNumber(client?.phoneNumber)}</td>
								<td>
									<span className={`status-pill status-${client.status === 1 ? 'Pending' : client.status === 2 ? 'Active' : 'Blocked'}`}>
										{client.status === 1 ? 'Pending' : client.status === 2 ? 'Active' : 'Blocked'}
									</span>
								</td>
								<td className="text-right">
									<button
										onClick={(e) => {
											e.stopPropagation();
											openClient(client);
										}}
										className="p-2 text-slate-400 hover:text-blue-600 transition-colors"
									>
										<File size={20} />
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>

				<div className="pagination-modern">
					<div className="pagination-info">
						Page <strong>{currentPage}</strong> of <strong>{totalPages || 1}</strong>
					</div>
					<div className="flex items-center gap-4">
						<select 
							className="px-2 py-1 rounded-md border border-slate-200 text-sm outline-none"
							value={pageSize} 
							onChange={handlePageSizeChange}
						>
							<option value="10">10 / page</option>
							<option value="50">50 / page</option>
							<option value="100">100 / page</option>
						</select>
						<div className="pagination-btns">
							<button onClick={handlePreviousPage} disabled={currentPage === 1}>
								<ArrowLeftCircle size={18} /> Prev
							</button>
							<button onClick={handleNextPage} disabled={currentPage >= totalPages}>
								Next <ArrowRightCircle size={18} />
							</button>
						</div>
					</div>
				</div>
			</div>
		</main>

		{selectedClient && (
			<ClientDetails
				selectedClient={selectedClient}
				clientDetails={clientById}
				onClose={closeSlide}
				closeSlide={closeSlide}
				newCLientDescription={newCLientDescription}
			/>
		)}

		{showCreateClientPanel && (
			<div className="client-hub-overlay">
				<div className="client-hub-overlay__backdrop" />
				<div className="client-hub-overlay__panel client-hub-overlay__panel--sheet">
					<ClientHubCreateClientPanel
						clients={clients}
						currentUserId={localStorage.getItem('userId') || ''}
						onCancel={() => setShowCreateClientPanel(false)}
						onClientReady={handleClientReady}
					/>
				</div>
			</div>
		)}
	</>
);
}
